import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  HostListener,
  inject,
  input,
  Input,
  OnInit,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { FormlyFieldConfig, FormlyForm } from '@ngx-formly/core';
import { GetTableDataService } from '../../services';
import { ActivatedRoute, Router } from '@angular/router';
import { debounceTime, distinctUntilChanged } from 'rxjs';
import {
  buildTableFilterGridConfig,
  columnCountForViewport,
  TABLE_FILTER_GRID_BREAKPOINTS,
  type TableFilterGridConfig,
} from './table-filter-grid.utils';

@Component({
  selector: 'lib-table-filter',
  imports: [ReactiveFormsModule, FormlyForm],
  standalone: true,
  template: `
    <div
      class="table-filter-inline"
      [formGroup]="form"
      [style.--table-filter-columns]="gridColumns"
    >
      <formly-form
        [form]="form"
        [fields]="formlyFields"
        [model]="model"
        class="table-filter-inline-fields"
      ></formly-form>
      <div class="table-filter-inline-actions">
        <ng-content select="[tableFilterExtraActions]" />
      </div>
    </div>
  `,
  styleUrl: './table-filter.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TableFilterComponent<T> implements OnInit, AfterViewInit {
  private _rawFieldConfigs: FormlyFieldConfig[] = [];

  @Input()
  set fields(value: FormlyFieldConfig[]) {
    this._rawFieldConfigs = value ?? [];
    // Fields kech kelganda (async options) query/string → number qayta qo‘llanadi
    this.initModelFromRouteAndService();
    this.rebuildFormlyFields();
    this.applyInitialQueryFiltersIfNeeded();
  }
  get fields(): FormlyFieldConfig[] {
    return this._rawFieldConfigs;
  }

  formlyFields: FormlyFieldConfig[] = [];
  @Input() ignoreDefaultFilterValuesInClear = false;
  @Input() ignoreFieldKeysInClear: string[] = [];
  /** Desktop uchun maks. ustun (default 7). */
  @Input() maxColumnsDesktop = TABLE_FILTER_GRID_BREAKPOINTS.desktop.maxColumns;
  @Input() maxColumnsTablet = TABLE_FILTER_GRID_BREAKPOINTS.tablet.maxColumns;
  @Input() maxColumnsMobile = TABLE_FILTER_GRID_BREAKPOINTS.mobile.maxColumns;
  /** Matn maydonlari uchun avto-qidiruv kechikishi (ms). */
  @Input() debounceMs = 400;
  // @Input() tableService!: GetTableDataService<T>;
  title = input<string>('');
  tableService = inject(GetTableDataService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);

  form = new FormGroup({});
  model: Record<string, any> = {};
  gridColumns = 1;
  /** Debug / kelajakda API grid_config bilan almashtirish uchun */
  gridConfig: TableFilterGridConfig | null = null;

  private leafFieldCount = 0;
  private destroyRef = inject(DestroyRef);
  private cdr = inject(ChangeDetectorRef);
  /** Formly qayta qurilganda valueChanges ortiqcha API chaqirmasligi uchun */
  private autoApplyPausedUntil = 0;
  /** F5 / query hydratatsiya — bir marta apply */
  private initialQueryApplied = false;

  ngOnInit() {
    this.initModelFromRouteAndService();
    this.applyInitialQueryFiltersIfNeeded();
  }

  private hasFilterQueryParams(): boolean {
    const queryParams = this.route.snapshot.queryParamMap;
    return this._rawFieldConfigs.some(
      (field) =>
        typeof field.key === 'string' &&
        field.hide !== true &&
        queryParams.has(field.key),
    );
  }

  private applyInitialQueryFiltersIfNeeded(): void {
    if (
      this.initialQueryApplied ||
      !this._rawFieldConfigs.length ||
      !this.hasFilterQueryParams()
    ) {
      return;
    }
    this.initialQueryApplied = true;
    this.applyFilters(false);
  }

  private initModelFromRouteAndService(): void {
    const queryParams = this.route.snapshot.queryParamMap;
    const modelFromQueryParams: Record<string, any> = {};

    this._rawFieldConfigs.forEach((field) => {
      const key = field.key as string;
      if (key && queryParams.has(key)) {
        modelFromQueryParams[key] = this.coerceModelValue(
          key,
          queryParams.get(key),
          field,
        );
      }
    });

    const filters = this.tableService.tableMetadata().filters as any;
    const parsed =
      typeof filters === 'string' ? JSON.parse(filters) : filters || {};

    const modelFromService = Object.keys(parsed).reduce(
      (acc, key) => {
        const field = this._rawFieldConfigs.find((f) => f.key === key);
        acc[key] = this.coerceModelValue(
          key,
          parsed[key]?.value ?? parsed[key],
          field,
        );
        return acc;
      },
      {} as Record<string, any>,
    );

    this.model = { ...modelFromService, ...modelFromQueryParams };
  }

  /**
   * URL query hamisha string. Select optionValue odatda number —
   * tip mos kelmasa p-select selected ko‘rsatmaydi (F5 dan keyin).
   */
  private coerceModelValue(
    key: string,
    val: unknown,
    field?: FormlyFieldConfig,
  ): unknown {
    if (val === null || val === undefined || val === '') {
      return val;
    }

    const fieldType = field?.type as string | undefined;
    const isSelectLike =
      fieldType === 'select' ||
      fieldType === 'multiselect' ||
      key.endsWith('Id') ||
      key === 'type' ||
      key === 'status';

    if (Array.isArray(val)) {
      return val.map((item) => this.coerceModelValue(key, item, field));
    }

    if (typeof val === 'string' && isSelectLike) {
      const trimmed = val.trim();
      if (trimmed !== '' && !Number.isNaN(Number(trimmed))) {
        return Number(trimmed);
      }
    }

    return this.coerceFilterValue(key, val);
  }

  private rebuildFormlyFields(): void {
    this.pauseAutoApply();
    // Grid faqat UI da ko‘rinadigan maydonlar bo‘yicha (hide: true hisobga olinmaydi)
    const leafFields = this._rawFieldConfigs;
    this.leafFieldCount = leafFields.filter((field) => field.hide !== true).length;

    this.gridConfig = buildTableFilterGridConfig(this.leafFieldCount, {
      mobile: { maxColumns: this.maxColumnsMobile, minWidth: 0 },
      tablet: {
        maxColumns: this.maxColumnsTablet,
        minWidth: TABLE_FILTER_GRID_BREAKPOINTS.tablet.minWidth,
      },
      desktop: {
        maxColumns: this.maxColumnsDesktop,
        minWidth: TABLE_FILTER_GRID_BREAKPOINTS.desktop.minWidth,
      },
    });
    this.syncGridColumns();

    const leafCopy = leafFields.map((field) => ({
      ...field,
      templateOptions: field.templateOptions
        ? { ...field.templateOptions }
        : undefined,
    }));

    this.applyInlineFieldWrapStyles(leafCopy);

    this.formlyFields = [
      {
        fieldGroupClassName: 'table-filter-field-group',
        fieldGroup: leafCopy,
      },
    ];

    this.applyAppendToBody(this.formlyFields);
    this.cdr.markForCheck();
  }

  ngAfterViewInit(): void {
    // Formly birinchi patch emitini skip(1) bilan emas, pause bilan yutamiz —
    // aks holda (init emit bo‘lmasa) foydalanuvchining 1-chi o‘zgarishi yo‘qoladi.
    this.pauseAutoApply();

    this.form.valueChanges
      .pipe(
        debounceTime(this.debounceMs),
        distinctUntilChanged(
          (prev, curr) => JSON.stringify(prev) === JSON.stringify(curr),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        if (Date.now() < this.autoApplyPausedUntil) {
          return;
        }
        // model ni qayta biriktirmaymiz — [model] bindingiga yangi referens
        // yuborilsa, formly-form buni tashqi o'zgarish deb hisoblab butun
        // formani qayta quradi va foydalanuvchi yozayotgan input fokusini
        // yo'qotadi ("qotib qolib yozilish" muammosi shundan).
        this.applyFilters(true, this.form.getRawValue());
      });
  }

  private pauseAutoApply(): void {
    this.autoApplyPausedUntil = Date.now() + this.debounceMs + 150;
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.syncGridColumns();
  }

  private syncGridColumns(): void {
    if (typeof window === 'undefined') {
      this.gridColumns = 1;
      return;
    }

    this.gridColumns = columnCountForViewport(
      this.leafFieldCount,
      window.innerWidth,
      {
        mobile: { maxColumns: this.maxColumnsMobile, minWidth: 0 },
        tablet: {
          maxColumns: this.maxColumnsTablet,
          minWidth: TABLE_FILTER_GRID_BREAKPOINTS.tablet.minWidth,
        },
        desktop: {
          maxColumns: this.maxColumnsDesktop,
          minWidth: TABLE_FILTER_GRID_BREAKPOINTS.desktop.minWidth,
        },
      },
    );
    this.cdr.markForCheck();
  }

  private buildPrimeNgFilters(model: Record<string, any>) {
    const filters: Record<string, { value: any }> = {};
    Object.entries(model).forEach(([key, val]) => {
      if (
        val === null ||
        val === undefined ||
        val === '' ||
        (Array.isArray(val) && val.length === 0)
      ) {
        return;
      }
      filters[key] = { value: this.coerceFilterValue(key, val) };
    });
    return filters;
  }

  /** Select/query-param values often arrive as strings; backend expects numbers for ids/enums. */
  private coerceFilterValue(key: string, val: unknown): unknown {
    const keepAsString = new Set([
      'search',
      'globalFilter',
      'studyYear',
      'subjectName',
      'schoolGrade',
      'organizationName',
      'institutionName',
      'fullName',
      'name',
    ]);
    if (keepAsString.has(key)) {
      return val;
    }
    if (Array.isArray(val)) {
      return val.map((item) => this.coerceFilterValue(key, item));
    }
    if (typeof val === 'number') {
      return val;
    }
    if (typeof val === 'string' && val.trim() !== '' && !Number.isNaN(Number(val))) {
      return Number(val);
    }
    return val;
  }

  private applyAppendToBody(fields: FormlyFieldConfig[]) {
    const needsAppendTo = new Set([
      'select',
      'multiselect',
      'autocomplete',
      'calendar',
    ]);

    const walk = (arr: FormlyFieldConfig[]) => {
      arr?.forEach((f) => {
        if (f.type && needsAppendTo.has(f.type as string)) {
          f.templateOptions = {
            ...(f.templateOptions ?? {}),
            appendTo: 'body',
          };
        }
        if (f.fieldGroup?.length) walk(f.fieldGroup);
      });
    };

    walk(fields);
  }

  private applyInlineFieldWrapStyles(fields: FormlyFieldConfig[]) {
    const walk = (arr: FormlyFieldConfig[]) => {
      arr?.forEach((f) => {
        if (f.fieldGroup?.length) {
          walk(f.fieldGroup);
          return;
        }
        const itemClass = 'table-filter-field-item';
        f.className = [f.className?.trim() ?? '', itemClass]
          .filter(Boolean)
          .join(' ');

        const to = (f.templateOptions ??= {});
        to['styleClass'] = [to['styleClass'] ?? '', 'w-full']
          .filter(Boolean)
          .join(' ');
        to['style'] = { ...(to['style'] ?? {}), width: '100%' };
      });
    };

    walk(fields);
  }

  private isEmptyValue(v: any): boolean {
    // null/undefined — bo'sh
    if (v === null || v === undefined) return true;

    // string — faqat probel bo'lsa bo'sh
    if (typeof v === 'string') return v.trim().length === 0;

    // array — uzunligi 0 bo'lsa bo'sh
    if (Array.isArray(v)) return v.length === 0;

    // object — (ixtiyoriy) ichki bo'sh obyektni bo'sh deb hisoblash
    if (typeof v === 'object') return Object.keys(v).length === 0;

    // number 0, boolean false — ODATDA qiymat deb olinadi (filtrlarda kerak bo'ladi)
    return false;
  }

  private extractDefaultFilterModel(): Record<string, any> {
    const defaults = (this.tableService.defaultFilter() || {}) as Record<
      string,
      unknown
    >;
    return Object.keys(defaults).reduce(
      (acc, key) => {
        const current = defaults[key] as { value?: unknown } | unknown;
        acc[key] =
          typeof current === 'object' && current !== null && 'value' in current
            ? (current as { value?: unknown }).value
            : current;
        return acc;
      },
      {} as Record<string, unknown>,
    );
  }

  private isSameValue(a: unknown, b: unknown): boolean {
    if (Array.isArray(a) && Array.isArray(b)) {
      if (a.length !== b.length) return false;
      return a.every((item, index) => item === b[index]);
    }
    return a === b;
  }

  hasAnyValue(form: FormGroup = this.form): boolean {
    const raw = form.getRawValue(); // { fieldA: ..., fieldB: ... }
    const defaultModel = this.ignoreDefaultFilterValuesInClear
      ? this.extractDefaultFilterModel()
      : {};

    return Object.entries(raw).some(([key, value]) => {
      if (this.ignoreFieldKeysInClear.includes(key)) {
        return false;
      }

      if (this.isEmptyValue(value)) {
        return false;
      }

      if (this.ignoreDefaultFilterValuesInClear) {
        return !this.isSameValue(value, defaultModel[key]);
      }

      return true;
    });
  }

  private updateQueryParams(model: Record<string, any>) {
    const queryParams = Object.fromEntries(
      this.collectFilterFieldKeys().map((key) => [
        key,
        this.isEmptyValue(model[key]) ? null : model[key],
      ]),
    );

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams,
      queryParamsHandling: 'merge',
      replaceUrl: true, // history ga yangi yozuv qo'shmasligi uchun
    });
  }

  applyFilters(updateUrl = true, modelOverride?: Record<string, any>) {
    const model = modelOverride ?? this.model;
    const updatedMetadata = {
      ...this.tableService.tableMetadata(),
      first: 0,
      filters: this.buildPrimeNgFilters(model),
    };
    this.tableService.tableMetadata.set(updatedMetadata);
    this.tableService.loadData(updatedMetadata);

    // Query params ga yozish
    if (updateUrl) {
      this.updateQueryParams(model);
    }
  }

  clearFilters() {
    const updatedMetadata = {
      ...this.tableService.tableMetadata(),
      filters: {},
      first: 0,
    };
    this.form.reset();
    this.model = {};
    this.tableService.tableMetadata.set(updatedMetadata);
    this.tableService.loadData(updatedMetadata);

    const clearedParams = Object.fromEntries(
      this.collectFilterFieldKeys().map((key) => [key, null]),
    );
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: clearedParams,
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  private collectFilterFieldKeys(): string[] {
    return this._rawFieldConfigs
      .map((field) => field.key)
      .filter((key): key is string => typeof key === 'string');
  }
}
